import { MobxManager } from 'framework/mobx-integration';
import { makeAutoObservable, runInAction } from 'mobx';
import { deserialize, serialize } from 'serializr';
import { apiFetch } from 'framework/api';
import { getUserHomeTabs } from '../api';
import { randomPassword } from 'tools/randomPassword';
import { User } from './user';

export interface UserConfigurationProps {
	user: Partial<User>;
	onChange: (user: Partial<User>) => void;
	onValidChange?: (valid: boolean) => void;
	mode: 'view' | 'update' | 'create';
	accountContext: string;
}

export class UserConfigurationStore {
	mobx = new MobxManager();
	user: User;

	constructor(public props: UserConfigurationProps) {
		makeAutoObservable(this, { props: false, mobx: false });
		this.user = deserialize(User, this.props.user);
		this.user.isNew = this.isCreateMode;
		this.init();
	}

	get validator() {
		return this.user.validator;
	}

	init() {
		this.mobx.reaction(
			() => serialize(this.user) as Partial<User>,
			(user) => this.props.onChange?.(user),
		);

		this.mobx.reaction(
			() => this.validator.valid,
			(valid) => this.props.onValidChange?.(valid),
			{ fireImmediately: true },
		);

		this.mobx.reaction(
			() => this.user.loginAccountId,
			(loginAccountId) => void this.loadLandingPages(loginAccountId),
		);

		this.mobx.reaction(
			() => this.user.lockUser,
			(lockUser) => {
				if (lockUser) {
					this.user.changePassword = false;
				}
			},
		);
	}

	validate() {
		this.validator.validate();

		const messages = [...new Set(this.validator.errors.flatMap((x) => x.errors))];
		return { isValid: this.validator.valid, message: messages.join('<br/>') };
	}

	async loadLandingPages(loginAccountId: string) {
		if (!this.user.id || !loginAccountId) {
			return;
		}

		const result = await apiFetch(getUserHomeTabs({ userId: this.user.id, loginAccountId }));
		if (result.success) {
			runInAction(() => {
				this.user.landingPages = result.data.map((x) => x.id);
			});
		}
	}

	get accountContext() {
		return this.props.accountContext;
	}

	get disabled() {
		return this.props.mode === 'view';
	}

	get isCreateMode() {
		return this.props.mode === 'create';
	}

	get activeUser() {
		return !this.user.lockUser;
	}

	set activeUser(value) {
		this.user.lockUser = !value;
	}

	get isAdminUser() {
		return this.user.isAdminUser;
	}

	get isCurrentUser() {
		return this.user.isCurrentUser;
	}

	get isLdapUser() {
		return this.user.authType === 'LDAP';
	}

	get showRoles() {
		return !this.isAdminUser;
	}

	get showActiveUserSwitch() {
		return !this.isCurrentUser;
	}

	get showPasswordFields() {
		return this.isCurrentUser || this.activeUser;
	}

	get showPasswordFlags() {
		return !this.isCurrentUser && this.activeUser;
	}

	get showOldPassword() {
		return this.isCurrentUser;
	}

	get passwordReadOnly() {
		return this.isLdapUser;
	}

	get generatePassword() {
		return this.generatedPasswordActive;
	}

	set generatePassword(value: boolean) {
		this.generatedPasswordActive = value;
		const password = value ? randomPassword() : '';
		this.user.password = password;
		this.user.passwordConfirm = password;
	}

	generatedPasswordActive = false;

	destroy() {
		this.mobx.destroy();
		this.user.validator.destroy();
	}
}
